<script setup lang="ts">
// Reusable "copy this text to the clipboard" icon button, shared by every
// message surface (web-ui Studio/Home/quick-ask + the extension thread) so each
// agent reply gets the same affordance from ONE component. The icon IS the
// feedback: it swaps to a check for ~1.5s on success (no toast noise). Icons
// come from the per-app injected set (useIcons), so this stays icon-agnostic.
//
// Copy writes TWO clipboard flavours in one ClipboardItem (the SOTA pattern - see
// MDN Clipboard.write): the paste target picks the richest it understands.
//   - text/html  = the RENDERED reply (cleaned via richHtmlFromElement), so pasting
//                   into Docs / Word / Slack / Gmail gets a formatted table + bold +
//                   structure, with math collapsed to readable LaTeX.
//   - text/plain = the markdown source, so code editors / other LLMs get clean
//                   re-renderable markdown (the long-standing Claude.ai behaviour).
// The rendered body isn't passed in: we self-discover it as the `.markstream-vue`
// root that precedes this button in its message container (overridable via
// `bodySelector`). If it can't be found, or ClipboardItem isn't available (insecure
// context / old browser), we fall back to a plain-text writeText of the markdown.
import { ref, onBeforeUnmount } from 'vue';
import Tooltip from './Tooltip.vue';
import { useIcons } from '../icons';
import { useI18n } from '../lib/i18n';
import { richHtmlFromElement } from '../lib/clipboardHtml';

const props = defineProps<{
  /** Markdown source - the text/plain half, and the writeText fallback. */
  text: string;
  /** Optional tooltip/aria override; defaults to the generic "Copy". */
  label?: string;
  /** Selector for the rendered body to copy as rich HTML (default: markstream root). */
  bodySelector?: string;
}>();

const icons = useIcons();
const { t } = useI18n();
const copied = ref(false);
const btn = ref<HTMLButtonElement | null>(null);
let timer: ReturnType<typeof setTimeout> | undefined;

// Find the rendered message body: the matching root that PRECEDES this button in the
// nearest ancestor that contains one. "Preceding" skips a trailing tool fold / actions
// row, so we grab the reply body and not some later sibling's.
function findBody(): HTMLElement | null {
  const sel = props.bodySelector ?? '.markstream-vue';
  const button = btn.value;
  let node: HTMLElement | null = button?.parentElement ?? null;
  while (node) {
    let best: HTMLElement | null = null;
    node.querySelectorAll<HTMLElement>(sel).forEach((r) => {
      if (button && r.compareDocumentPosition(button) & Node.DOCUMENT_POSITION_FOLLOWING) best = r;
    });
    if (best) return best;
    node = node.parentElement;
  }
  return null;
}

function flagCopied(): void {
  copied.value = true;
  clearTimeout(timer);
  timer = setTimeout(() => { copied.value = false; }, 1500);
}

async function copy(): Promise<void> {
  const md = (props.text ?? '').trim();
  const body = findBody();
  if (!md && !body) return;
  try {
    const canRich = typeof ClipboardItem !== 'undefined' && !!navigator.clipboard?.write && !!body;
    if (canRich && body) {
      const html = richHtmlFromElement(body);
      await navigator.clipboard.write([new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([md], { type: 'text/plain' }),
      })]);
    } else {
      await navigator.clipboard.writeText(md);
    }
    flagCopied();
  } catch {
    // Rich write rejected (unsupported type / permission)? Try plain text before
    // giving up - that path is far more broadly allowed.
    try { await navigator.clipboard.writeText(md); flagCopied(); } catch { /* blocked; no chrome */ }
  }
}

onBeforeUnmount(() => clearTimeout(timer));
</script>

<template>
  <Tooltip :label="copied ? t('common.copied') : (label ?? t('common.copy'))">
    <button
      ref="btn"
      type="button"
      class="hq-copy"
      :class="{ 'hq-copy--done': copied }"
      :aria-label="copied ? t('common.copied') : (label ?? t('common.copy'))"
      @click="copy"
    >
      <component :is="copied ? icons.check : icons.copy" :size="14" />
    </button>
  </Tooltip>
</template>

<style scoped>
.hq-copy {
  display: inline-grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: var(--radius-input, 8px);
  background: transparent;
  color: var(--ink-subtle, #8a8a8a);
  cursor: pointer;
  opacity: 0.6;
  transition: background 0.12s ease, color 0.12s ease, opacity 0.12s ease;
}
.hq-copy:hover { background: var(--surface-sunken, rgba(0, 0, 0, 0.06)); color: var(--ink-primary, currentColor); opacity: 1; }
.hq-copy:focus-visible { outline: none; box-shadow: 0 0 0 2px var(--accent-default, #4b9fff); opacity: 1; }
.hq-copy--done { color: var(--accent-default, #4b9fff); opacity: 1; }
</style>
